import React, { useState, useEffect } from "react";
import moment from "moment";
import { cover } from "polished";
import { mdiClose } from "@mdi/js";

import { graphql, StaticQuery } from "gatsby";
import { RichText as PrismicRichText } from "prismic-reactjs";

import {
  Icon,
  AbrioFact,
  FlexGrid,
  H6,
  PageHeader,
  SectionWrapper,
  SEO,
  Share,
  Text,
  FlexRow,
  Button
} from "src/components";

import serializer from "src/utils/serializer";
import linkResolver from "src/utils/linkResolver";

const NewsItem = ({
  date,
  source,
  heading,
  video,
  tags,
  link,
  setSelectedTag
}) => (
  <article
    css={`
      padding: 24px 12px 24px;
      border-bottom: 2px solid ${props => props.theme.colors.text.default};
      &:first-child {
        border-top: 2px solid ${props => props.theme.colors.text.default};
      }
    `}
  >
    <H6
      css={`
        font-weight: 400;
      `}
    >
      {date}
    </H6>
    <H6>{source}</H6>
    <Share link={link} />
    <Text
      as="h1"
      fontSize={2.25}
      css={`
        margin-top: 8px;
      `}
    >
      <a href={link} target="_blank" rel="noopener noreferrer">
        {heading}
      </a>
    </Text>
    {video && (
      <div
        css={`
          max-width: 512px;
        `}
      >
        <div
          dangerouslySetInnerHTML={{ __html: video }}
          css={`
            position: relative;
            margin: 16px 0;
            padding-bottom: ${(270 / 480) * 100}%;
            iframe {
              ${cover()};
              width: 100%;
              height: 100%;
            }
          `}
        />
      </div>
    )}
    <Text
      color="brand.blue"
      css={`
        margin-top: 8px;
      `}
    >
      {tags.map((tag, index) => {
        if (tag.tag) {
          return (
            <span
              css={`
                cursor: pointer;
              `}
              onClick={() => {
                setSelectedTag(tag.tag);
              }}
            >
              {(index > 0 ? ", " : "") + tag.tag}
            </span>
          );
        } else {
          return null;
        }
      })}
    </Text>
  </article>
);

function MainContent({ description, news }) {
  const [sortedNews, setSortedNews] = useState([]);
  const [selectedTag, setSelectedTag] = useState("");
  const [count, setCount] = useState(10);

  function loadMore() {
    setCount(count + 10);
  }

  useEffect(() => {
    let finalNews = [];
    if (selectedTag === "") {
      finalNews = news;
    } else {
      news.forEach(newsItem => {
        if (newsItem.items) {
          newsItem.items.forEach(item => {
            if (item.tag === selectedTag) {
              finalNews.push(newsItem);
            }
          });
        }
      });
    }
    finalNews = finalNews.sort((a, b) => {
      return moment(a.primary.date).unix() > moment(b.primary.date).unix()
        ? -1
        : 1;
    });
    setSortedNews(finalNews);
    document.getElementById("newsFeed").scrollIntoView();
  }, [news, selectedTag]);

  return (
    <article
      css={`
        width: 67%;
        @media (max-width: 64rem) {
          width: 100%;
        }
      `}
    >
      <section
        css={`
          margin: 64px 0;
        `}
        id="newsFeed"
      >
        <Text fontSize={3}>{description}</Text>
      </section>
      <div>
        {selectedTag && (
          <FlexRow
            mb={4}
            alignItems="center"
            onClick={() => {
              setSelectedTag("");
            }}
            style={{ cursor: "pointer" }}
          >
            <Text
              children={"Filtering by: " + selectedTag}
              fontSize={2}
              mr={2}
              fontWeight={600}
              color="brand.blue"
            />
            <Icon
              symbol={mdiClose}
              size={1.5}
              color="#25CAD3"
              css={`
                margin-top: 2px;
              `}
            />
          </FlexRow>
        )}
        {sortedNews && (
          <section>
            {sortedNews.slice(0, count).map((newsItem, index) => (
              <NewsItem
                date={moment(newsItem.primary.date).format("MMMM DD, YYYY")}
                source={newsItem.primary.outlet}
                heading={newsItem.primary.excerpt}
                tags={newsItem.items}
                video={
                  newsItem.primary.video &&
                  newsItem.primary.video.html &&
                  newsItem.primary.video.html
                }
                key={"newsItem" + index}
                link={
                  newsItem.primary.news_link && newsItem.primary.news_link.url
                }
                setSelectedTag={setSelectedTag}
              />
            ))}
            {sortedNews.length > count && (
              <FlexRow justifyContent="center" my={4}>
                <Button children={`Load more`} onClick={loadMore} />
              </FlexRow>
            )}
          </section>
        )}
      </div>
    </article>
  );
}

const Body = ({ description, news, factNumber, factIllustration, fact }) => (
  <SectionWrapper>
    <FlexGrid gutterX={[8]} gutterY={[6]}>
      <MainContent description={description} news={news} />
      {factIllustration && (
        <AbrioFact
          number={factNumber}
          children={fact}
          color="brand.orange"
          type={factIllustration}
        />
      )}
    </FlexGrid>
  </SectionWrapper>
);

const NewsPage = ({ preview }) => (
  <StaticQuery
    query={graphql`
      {
        prismicNews {
          dataString
        }
      }
    `}
    render={data => {
      let page = JSON.parse(data.prismicNews.dataString);
      if (preview) {
        page = preview;
      }
      return (
        <>
          <SEO
            image={page.seo_image && page.seo_image.url}
            title={page.seo_title && PrismicRichText.asText(page.seo_title)}
            description={
              page.seo_description &&
              PrismicRichText.asText(page.seo_description)
            }
          />
          <PageHeader
            heading={page.header && PrismicRichText.asText(page.header)}
            color="brand.orange"
          />
          <Body
            description={
              page.page_description &&
              PrismicRichText.render(
                page.page_description,
                linkResolver,
                serializer
              )
            }
            factIllustration={page.fact_illustration}
            factNumber={page.fact_number}
            fact={
              page.fact &&
              PrismicRichText.render(page.fact, linkResolver, serializer)
            }
            news={page.body}
          />
        </>
      );
    }}
  />
);

export default NewsPage;
